import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Play, Pause, Trash2, MoreVertical, Eye, RefreshCw } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { alertsAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const AlertCard = ({ alert, onUpdate, onDelete, onViewResults }) => {
  const [loading, setLoading] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-gray-100 text-gray-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleTogglePause = async () => {
    setLoading(true);
    try {
      const result = await alertsAPI.togglePause(alert.id || alert._id);
      toast({
        title: result.status === 'paused' ? 'Alert Paused' : 'Alert Resumed',
        description: result.status === 'paused' 
          ? 'This alert will not run until you resume it'
          : 'This alert will run on schedule',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update alert status',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRunNow = async () => {
    setLoading(true);
    try {
      await alertsAPI.runNow(alert.id || alert._id);
      toast({
        title: 'Alert Triggered',
        description: 'The alert has been queued and will run shortly',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to trigger alert',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    setLoading(true);
    try {
      await alertsAPI.delete(alert.id || alert._id);
      toast({
        title: 'Alert Deleted',
        description: 'The alert has been removed',
      });
      onDelete();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete alert',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-5 hover:shadow-lg transition-shadow relative">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Badge className={getStatusColor(alert.status)}>
              {alert.status}
            </Badge>
            {alert.is_first_scrape && (
              <Badge variant="outline" className="text-xs">
                New
              </Badge>
            )}
          </div>
          <h3 className="font-semibold text-lg text-gray-900 mb-1">
            {alert.query || 'URL-based alert'}
          </h3>
          {alert.query && (
            <p className="text-sm text-gray-600">
              {alert.from_price && alert.to_price
                ? `S$${alert.from_price} - S$${alert.to_price}`
                : alert.from_price
                ? `Min S$${alert.from_price}`
                : alert.to_price
                ? `Max S$${alert.to_price}`
                : 'All prices'}
            </p>
          )}
          {alert.url && (
            <p className="text-sm text-gray-600 truncate max-w-md">
              {alert.url}
            </p>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" disabled={loading} className="shrink-0">
              <MoreVertical className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-50">
            <DropdownMenuItem onClick={() => onViewResults(alert)}>
              <Eye className="w-4 h-4 mr-2" />
              View Results
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleRunNow}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Run Now
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleTogglePause}>
              {alert.status === 'paused' ? (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDelete} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center justify-between pt-3 border-t text-sm">
        <div className="text-gray-600">
          {alert.last_run ? (
            <span>Last run {formatDistanceToNow(new Date(alert.last_run), { addSuffix: true })}</span>
          ) : (
            <span>Never run</span>
          )}
        </div>
        <div className="text-gray-500">
          Expires {formatDistanceToNow(new Date(alert.expire_at), { addSuffix: true })}
        </div>
      </div>
    </Card>
  );
};

export default AlertCard;
